/** Stage 40 · Global Launch Readiness Centre.
 *  Tabs: Rollup · Workstreams · Packages · Capability Matrix · Gates. */
import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Rocket, ClipboardCheck, Layers, Package as PackageIcon,
  Flag, Plus, CheckCircle2, Circle, ShieldCheck,
} from "lucide-react";

const WS_BADGE = {
  not_started:  "bg-slate-100 text-slate-600 border-slate-200",
  in_progress:  "bg-blue-100 text-blue-800 border-blue-200",
  at_risk:      "bg-amber-100 text-amber-800 border-amber-200",
  blocked:      "bg-rose-100 text-rose-800 border-rose-200",
  ready:        "bg-emerald-100 text-emerald-800 border-emerald-200",
  done:         "bg-emerald-100 text-emerald-800 border-emerald-200",
};

const GATE_BADGE = {
  pending:   "bg-slate-100 text-slate-600 border-slate-200",
  in_review: "bg-blue-100 text-blue-800 border-blue-200",
  passed:    "bg-emerald-100 text-emerald-800 border-emerald-200",
  failed:    "bg-rose-100 text-rose-800 border-rose-200",
  waived:    "bg-amber-100 text-amber-800 border-amber-200",
};

const CAP_STYLE = {
  included: "bg-emerald-50 text-emerald-800 border-emerald-200",
  addon:    "bg-amber-50 text-amber-800 border-amber-200",
  na:       "bg-slate-50 text-slate-400 border-slate-200",
};

export default function LaunchReadiness() {
  const [tab, setTab] = useState("rollup");
  return (
    <div className="max-w-6xl space-y-5" data-testid="launch-page">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">
          Administration · Global Launch Readiness
        </div>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">
          Launch Readiness Centre
        </h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Workstreams, packaging, capability matrix and launch gates — the operating model for
          going from private pilots to enterprise general availability.
        </p>
      </div>

      <nav className="flex flex-wrap gap-1 border-b border-slate-200">
        {[
          { k: "rollup",       l: "Rollup",             I: Rocket },
          { k: "workstreams",  l: "Workstreams",        I: ClipboardCheck },
          { k: "packages",     l: "Product Packages",   I: PackageIcon },
          { k: "capabilities", l: "Capability Matrix",  I: Layers },
          { k: "gates",        l: "Launch Gates",       I: Flag },
        ].map(({ k, l, I }) => (
          <button key={k} onClick={() => setTab(k)}
            data-testid={`launch-tab-${k}`}
            className={
              "px-3 py-2 text-sm inline-flex items-center gap-1.5 border-b-2 -mb-px " +
              (tab === k
                ? "border-slate-900 text-slate-900 font-medium"
                : "border-transparent text-slate-500 hover:text-slate-800")
            }>
            <I className="h-4 w-4" /> {l}
          </button>
        ))}
      </nav>

      {tab === "rollup"       && <Rollup />}
      {tab === "workstreams"  && <Workstreams />}
      {tab === "packages"     && <Packages />}
      {tab === "capabilities" && <Capabilities />}
      {tab === "gates"        && <Gates />}
    </div>
  );
}

/* -------------------- Rollup -------------------- */
function Rollup() {
  const [r, setR] = useState(null);
  useEffect(() => {
    api.get("/launch/rollup").then(({ data }) => setR(data)).catch(() => setR(null));
  }, []);
  if (!r) return <div className="text-sm text-slate-500 py-6">Loading rollup…</div>;
  const verdictColor = ({
    "LAUNCH READY": "text-emerald-700",
    "CONDITIONAL":  "text-amber-700",
    "IN PROGRESS":  "text-blue-700",
    "NOT READY":    "text-rose-700",
  })[r.verdict] || "text-slate-700";

  return (
    <div className="space-y-4" data-testid="launch-rollup">
      <div className="bg-white border border-slate-200 rounded-md p-5 flex items-center gap-5">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Overall readiness</div>
          <div className="text-4xl font-heading font-semibold text-slate-900 tabular-nums mt-1"
               data-testid="launch-overall-score">{r.overall_score}</div>
        </div>
        <div className="border-l border-slate-200 pl-5">
          <div className={"text-lg font-medium " + verdictColor}
               data-testid="launch-verdict">{r.verdict}</div>
          <div className="text-xs text-slate-500 mt-1">
            {r.counts.workstreams_ready}/{r.counts.workstreams} workstreams ready ·{" "}
            {r.counts.gates_passed}/{r.counts.gates} gates passed
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Workstream score"  value={`${r.workstream_score}`} />
        <StatCard label="Gate score"        value={`${r.gate_score}`} />
        <StatCard label="Blocked / at risk" value={r.counts.workstreams_blocked} tone="warn" />
        <StatCard label="Gates failed"      value={r.counts.gates_failed}
                  tone={r.counts.gates_failed ? "bad" : "ok"} />
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">By workstream status</div>
        <div className="flex flex-wrap gap-2 text-xs">
          {Object.entries(r.by_workstream_status || {}).map(([k, v]) => (
            <span key={k}
              className={"px-2 py-1 rounded border " + (WS_BADGE[k] || WS_BADGE.not_started)}>
              {k.replace(/_/g, " ")} · <b className="tabular-nums">{v}</b>
            </span>
          ))}
          {Object.keys(r.by_workstream_status || {}).length === 0 && (
            <span className="text-slate-500">No workstreams yet.</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Packages</div>
          <div className="text-2xl font-heading font-semibold text-slate-900 tabular-nums mt-1">
            {r.counts.packages}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Capabilities mapped</div>
          <div className="text-2xl font-heading font-semibold text-slate-900 tabular-nums mt-1">
            {r.counts.capabilities}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone = "default" }) {
  const border = tone === "bad" ? "border-rose-200"
    : tone === "warn" ? "border-amber-200"
    : tone === "ok"   ? "border-emerald-200"
    : "border-slate-200";
  return (
    <div className={`rounded-md bg-white border ${border} p-3`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-2xl font-heading font-semibold text-slate-900 tabular-nums mt-1">{value}</div>
    </div>
  );
}

/* -------------------- Workstreams -------------------- */
function Workstreams() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ workstreams: [], statuses: [] });
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/launch/workstreams");
    setItems(data.items || []);
    setMeta({ workstreams: data.workstreams || [], statuses: data.statuses || [] });
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (wid, idx) => {
    try { await api.post(`/launch/workstreams/${wid}/milestones/${idx}/toggle`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3" data-testid="launch-workstreams">
      <div className="flex justify-end">
        <button onClick={() => setShowForm(true)}
          className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white inline-flex items-center gap-1"
          data-testid="launch-ws-new">
          <Plus className="h-4 w-4" /> New workstream
        </button>
      </div>
      {showForm && (
        <WorkstreamForm meta={meta} onCancel={() => setShowForm(false)}
                        onSaved={() => { setShowForm(false); load(); }} />
      )}
      <div className="space-y-2">
        {items.length === 0 && (
          <div className="text-sm text-slate-500 py-6 text-center bg-white border border-slate-200 rounded-md">
            No workstreams yet.
          </div>
        )}
        {items.map((w) => (
          <div key={w.id}
            className="bg-white border border-slate-200 rounded-md p-3"
            data-testid={`launch-ws-${w.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-900">{w.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {w.workstream} · {w.owner || "unassigned"}
                  {w.target_date ? ` · target ${w.target_date}` : ""}
                </div>
                {w.summary && <div className="text-xs text-slate-600 mt-1">{w.summary}</div>}
              </div>
              <span className={"text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded border " + (WS_BADGE[w.status] || WS_BADGE.not_started)}>
                {(w.status || "").replace(/_/g, " ")}
              </span>
            </div>
            {(w.milestones || []).length > 0 && (
              <ul className="mt-2 space-y-1">
                {(w.milestones || []).map((m, i) => (
                  <li key={i}>
                    <button
                      onClick={() => toggle(w.id, i)}
                      data-testid={`launch-ws-${w.id}-milestone-${i}`}
                      className="text-xs inline-flex items-center gap-1.5 text-slate-700 hover:text-slate-900">
                      {m.done
                        ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                        : <Circle className="h-3.5 w-3.5 text-slate-400" />}
                      <span className={m.done ? "line-through text-slate-500" : ""}>{m.label}</span>
                      {m.due && <span className="text-slate-400">· {m.due}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {(w.risks || []).length > 0 && (
              <div className="mt-2 text-xs text-rose-700">
                <b>Risks:</b> {(w.risks || []).join(" · ")}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function WorkstreamForm({ meta, onCancel, onSaved }) {
  const [f, setF] = useState({
    name: "", workstream: (meta.workstreams || [])[0] || "product",
    owner: "", status: "not_started", target_date: "",
    summary: "", milestones: "",
  });
  const save = async () => {
    try {
      const payload = {
        ...f,
        milestones: (f.milestones || "").split("\n").map((s) => s.trim())
          .filter(Boolean).map((s) => ({ label: s, done: false })),
      };
      await api.post("/launch/workstreams", payload);
      toast.success("Workstream saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2"
         data-testid="launch-ws-form">
      <div className="grid grid-cols-2 gap-2">
        <Field label="Name">
          <input className="input" value={f.name}
                 onChange={(e) => setF({ ...f, name: e.target.value })}
                 data-testid="launch-ws-name" />
        </Field>
        <Field label="Workstream">
          <select className="input" value={f.workstream}
                  onChange={(e) => setF({ ...f, workstream: e.target.value })}>
            {(meta.workstreams || []).map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
        </Field>
        <Field label="Owner">
          <input className="input" value={f.owner}
                 onChange={(e) => setF({ ...f, owner: e.target.value })} />
        </Field>
        <Field label="Status">
          <select className="input" value={f.status}
                  onChange={(e) => setF({ ...f, status: e.target.value })}>
            {(meta.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Target date">
          <input className="input" type="date" value={f.target_date}
                 onChange={(e) => setF({ ...f, target_date: e.target.value })} />
        </Field>
        <Field label="Summary" full>
          <input className="input" value={f.summary}
                 onChange={(e) => setF({ ...f, summary: e.target.value })} />
        </Field>
        <Field label="Milestones (one per line)" full>
          <textarea className="input min-h-[80px]" value={f.milestones}
                    onChange={(e) => setF({ ...f, milestones: e.target.value })} />
        </Field>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className="h-9 px-3 text-sm border border-slate-200 rounded-md">
          Cancel
        </button>
        <button onClick={save} className="h-9 px-3 text-sm bg-slate-900 text-white rounded-md"
                data-testid="launch-ws-save">Save</button>
      </div>
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <label className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

/* -------------------- Packages -------------------- */
function Packages() {
  const [items, setItems] = useState([]);
  const [tiers, setTiers] = useState([]);
  const load = useCallback(async () => {
    const { data } = await api.get("/launch/packages");
    setItems(data.items || []); setTiers(data.tiers || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3" data-testid="launch-packages">
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        {items.length === 0 && (
          <div className="col-span-full text-sm text-slate-500 py-6 text-center bg-white border border-slate-200 rounded-md">
            No packages yet.
          </div>
        )}
        {items.map((p) => (
          <div key={p.id}
               className="bg-white border border-slate-200 rounded-md p-4"
               data-testid={`launch-pkg-${p.tier}`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{p.tier}</div>
            <div className="text-lg font-heading font-semibold text-slate-900 mt-1">{p.name}</div>
            <div className="text-xs text-slate-500 mt-0.5 font-mono">{p.sku}</div>
            {p.positioning && (
              <div className="text-xs text-slate-600 mt-2">{p.positioning}</div>
            )}
            <div className="mt-3 text-sm">
              <span className="text-slate-500">Price:</span>{" "}
              <span className="text-slate-900 tabular-nums">
                {p.list_price != null ? `${p.currency} ${p.list_price}` : "Custom"}
              </span>
              <span className="text-xs text-slate-500"> · {p.price_model || "—"}</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              {(p.entitlements || []).map((e) => (
                <span key={e}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
                  {e}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-slate-400">
        Tiers: {tiers.join(" · ")}
      </div>
    </div>
  );
}

/* -------------------- Capability matrix -------------------- */
function Capabilities() {
  const [items, setItems] = useState([]);
  const [tiers, setTiers] = useState([]);
  useEffect(() => {
    api.get("/launch/capabilities").then(({ data }) => {
      setItems(data.items || []); setTiers(data.tiers || []);
    });
  }, []);
  return (
    <div className="space-y-3" data-testid="launch-capabilities">
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
            <tr>
              <th className="text-left py-2 px-3">Module</th>
              {tiers.map((t) => (
                <th key={t} className="text-left py-2 px-3">{t}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={1 + tiers.length}
                    className="py-6 text-center text-slate-500">
                  No capabilities mapped.
                </td>
              </tr>
            ) : items.map((c) => (
              <tr key={c.id} className="border-t border-slate-100"
                  data-testid={`launch-cap-${c.module}`}>
                <td className="py-2 px-3">
                  <div className="text-slate-900">{c.label}</div>
                  <div className="text-[10px] font-mono text-slate-500">{c.module}</div>
                </td>
                {tiers.map((t) => {
                  const v = (c.tiers || {})[t] || "na";
                  return (
                    <td key={t} className="py-2 px-3">
                      <span className={"text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " + (CAP_STYLE[v] || CAP_STYLE.na)}>
                        {v}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* -------------------- Launch Gates -------------------- */
function Gates() {
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    const { data } = await api.get("/launch/gates");
    setItems(data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (gid, idx) => {
    try { await api.post(`/launch/gates/${gid}/checklist/${idx}/toggle`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const signoff = async (gid, outcome) => {
    try {
      await api.post(`/launch/gates/${gid}/signoff?outcome=${outcome}`);
      toast.success(`Gate ${outcome}`); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3" data-testid="launch-gates">
      {items.length === 0 && (
        <div className="text-sm text-slate-500 py-6 text-center bg-white border border-slate-200 rounded-md">
          No launch gates configured.
        </div>
      )}
      {items.map((g) => {
        const done = (g.checklist || []).filter((c) => c.done).length;
        const total = (g.checklist || []).length;
        const pct = total ? Math.round((done / total) * 100) : 0;
        return (
          <div key={g.id}
               className="bg-white border border-slate-200 rounded-md p-4"
               data-testid={`launch-gate-${g.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-900 inline-flex items-center gap-1.5">
                  <ShieldCheck className="h-4 w-4 text-slate-500" /> {g.name}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {g.scope || "—"} · {g.owner || "unassigned"}
                  {g.target_date ? ` · target ${g.target_date}` : ""}
                </div>
              </div>
              <span className={"text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded border " + (GATE_BADGE[g.status] || GATE_BADGE.pending)}>
                {(g.status || "").replace(/_/g, " ")}
              </span>
            </div>

            <div className="mt-3">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-slate-500 mb-1">
                <span>Checklist</span>
                <span className="tabular-nums">{done}/{total} · {pct}%</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded overflow-hidden">
                <div className="h-full bg-slate-900" style={{ width: `${pct}%` }} />
              </div>
              <ul className="mt-2 space-y-1">
                {(g.checklist || []).map((c, i) => (
                  <li key={i}>
                    <button onClick={() => toggle(g.id, i)}
                      data-testid={`launch-gate-${g.id}-check-${i}`}
                      className="text-xs inline-flex items-center gap-1.5 text-slate-700 hover:text-slate-900">
                      {c.done
                        ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                        : <Circle className="h-3.5 w-3.5 text-slate-400" />}
                      <span className={c.done ? "line-through text-slate-500" : ""}>{c.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {(g.approvers || []).length > 0 && (
              <div className="mt-2 text-xs text-slate-600">
                <b>Approvers:</b> {(g.approvers || []).join(" · ")}
              </div>
            )}

            <div className="mt-3 flex gap-2 flex-wrap">
              <button onClick={() => signoff(g.id, "passed")}
                className="h-8 px-3 text-xs rounded-md bg-emerald-700 text-white hover:bg-emerald-800"
                data-testid={`launch-gate-${g.id}-pass`}>Mark passed</button>
              <button onClick={() => signoff(g.id, "failed")}
                className="h-8 px-3 text-xs rounded-md bg-rose-700 text-white hover:bg-rose-800">
                Mark failed
              </button>
              <button onClick={() => signoff(g.id, "waived")}
                className="h-8 px-3 text-xs rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50">
                Waive
              </button>
              {g.signed_by && (
                <span className="text-[10px] uppercase tracking-widest text-slate-500 self-center">
                  signed off by {g.signed_by} · {(g.signed_at || "").slice(0, 10)}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
